import { useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTemplateListQuery } from '@/client/api/campaigns/campaigns.query';
import { useUpdateTemplateMutation } from '@/client/api/campaigns/campaigns.mutation';
import { UpdateTemplatePayload } from '@/client/types/campaigns';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import { getApiErrorMessage } from '@/client/utils/errors';
import { MESSAGING_TEMPLATES_HREF } from './deliveryUi';

export default function MapLocalTemplateDialog({
  open,
  onOpenChange,
  providerLabel,
  templateId,
  templateIdColumn,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerLabel: string;
  templateId: string | number | null;
  templateIdColumn?: string | null;
}) {
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<number | null>(null);
  const listQ = useTemplateListQuery({ limit: 100, offset: 0 }, open);
  const update = useUpdateTemplateMutation();

  const templates = useMemo(() => {
    const list = listQ.data?.templates ?? [];
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter((t) =>
      [t.name_system, t.name, t.ntmplid, t.locale].filter(Boolean).join(' ').toLowerCase().includes(term),
    );
  }, [listQ.data, q]);

  const column = templateIdColumn?.trim() || null;

  const handleMap = async () => {
    if (!column) {
      toast.error('This provider has no template_id_column — map it on the messaging templates page.');
      return;
    }
    if (selected == null || templateId == null) return;
    try {
      await update.mutateAsync({
        ntmplid: selected,
        body: { [column]: String(templateId) } as UpdateTemplatePayload,
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success(`Mapped local template #${selected} → ${providerLabel} ${templateId}`, {
        richColors: true,
      });
      onOpenChange(false);
      setSelected(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Mapping failed'), { richColors: true });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Map to a local template</DialogTitle>
          <DialogDescription>
            Step 4 makes this live: set{' '}
            <code className="rounded bg-gray-100 px-1 text-xs">{column || 'provider_template_id'}</code> on
            a <code className="rounded bg-gray-100 px-1 text-xs">notification_template</code> row. Skipping
            it leaves the vendor template created but inert.
          </DialogDescription>
        </DialogHeader>

        {!column ? (
          <p className="text-sm text-amber-800">
            This provider has no <code>template_id_column</code>. Map it on the{' '}
            <Link to={MESSAGING_TEMPLATES_HREF} className="underline">
              messaging templates
            </Link>{' '}
            page instead.
          </p>
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter name_system…"
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="max-h-72 overflow-y-auto rounded-md border border-gray-100">
              {listQ.isLoading ? (
                <p className="p-4 text-center text-sm text-gray-500">Loading local templates…</p>
              ) : templates.length === 0 ? (
                <p className="p-4 text-center text-sm text-gray-500">No local templates found.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {templates.map((t) => {
                    const current = (t as Record<string, unknown>)[column];
                    const checked = selected === t.ntmplid;
                    return (
                      <li key={t.ntmplid}>
                        <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-gray-50">
                          <input
                            type="radio"
                            name="ntmplid"
                            checked={checked}
                            onChange={() => setSelected(t.ntmplid)}
                            className="mt-1"
                          />
                          <span className="min-w-0">
                            <span className="block text-sm font-medium text-gray-900">
                              {t.name_system}
                            </span>
                            <span className="block text-xs text-gray-500">
                              #{t.ntmplid}
                              {t.locale ? ` · ${t.locale}` : ''}
                              {current
                                ? ` · current ${column}=${String(current)}`
                                : ` · ${column} unset`}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!column || selected == null || update.isPending || templateId == null}
            onClick={handleMap}
            className="gap-2"
          >
            {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Map &amp; go live
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
